import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8002';

/**
 * Post-sign confirmation page. The signing token is single-use for the
 * sign action itself, but stays valid for download-only of the signed
 * PDF until the original expires_at window closes — so the same URL
 * the tenant landed on continues to work for the download link below.
 */
const PublicLeaseSignDone = () => {
  const { token } = useParams();
  const [info, setInfo] = useState(null);

  useEffect(() => {
    // Lookup may legitimately 404 here (token rotated on submit). The
    // download endpoint will still work since it reads `signed_token`.
    // We try the lookup once just to get the customer email / facility
    // name from the same-window cache, but absence is not an error.
    let cancelled = false;
    axios
      .get(`${API_URL}/api/public/leases/sign/${token}`)
      .then((res) => !cancelled && setInfo(res.data))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [token]);

  return (
    <div className="min-h-screen bg-[#FAFBFC] flex items-center justify-center px-4 py-10">
      <div className="max-w-md w-full rounded-xl border border-slate-200/80 bg-white p-8 text-center">
        <div className="flex justify-center mb-4">
          <CheckCircle2 className="w-12 h-12 text-emerald-500" />
        </div>
        <h1 className="text-[18px] font-semibold text-slate-900">
          Lease signed
        </h1>
        <p className="text-[13px] text-slate-500 mt-2">
          {info?.customer_email ? (
            <>A signed copy has been emailed to {info.customer_email}.</>
          ) : (
            <>A signed copy will be emailed to you shortly.</>
          )}
        </p>

        <div className="mt-6 rounded-lg bg-slate-50 px-4 py-3 text-left">
          <p className="text-[12px] font-medium text-slate-500 uppercase tracking-wide mb-1">
            Next step
          </p>
          <p className="text-[13px] text-slate-700">
            Complete your move-in payment to activate your unit. Check your
            email for payment instructions.
          </p>
        </div>

        <a
          href={`${API_URL}/api/public/leases/sign/${token}/signed-pdf`}
          className="mt-6 inline-block w-full rounded-lg bg-blue-600 px-4 py-2.5 text-[13px] font-medium text-white hover:bg-blue-700 transition-colors"
          download="lease_signed.pdf"
        >
          Download signed lease PDF
        </a>
      </div>
    </div>
  );
};

export default PublicLeaseSignDone;
